import { useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Building2, LogOut, ChevronLeft, ChevronRight, ShieldAlert, ImageIcon, Mail, Scale, Headset, Activity, ClipboardList, Megaphone, UserMinus, PanelsTopLeft } from 'lucide-react'

export default function SuperAdminLayout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  // Wide data tables use the full width; everything else stays in a readable column.
  const isWidePage = ['/superadmin/tenants', '/superadmin/cancellations'].includes(pathname.replace(/\/$/, ''))
  const [collapsed, setCollapsed] = useState(false)

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <div className="flex h-screen bg-slate-100">
      {/* Sidebar */}
      <aside className={`${collapsed ? 'w-16' : 'w-60'} bg-slate-900 flex flex-col shrink-0 transition-all duration-200`}>
        {/* Header */}
        <div className={`flex items-center gap-2.5 px-4 py-5 border-b border-slate-700 ${collapsed ? 'justify-center' : ''}`}>
          <ShieldAlert size={20} className="text-red-400 shrink-0" />
          {!collapsed && (
            <div>
              <div className="text-xs font-bold text-red-400 uppercase tracking-widest leading-none">Super Admin</div>
              <div className="text-xs text-slate-400 mt-0.5">Platform management</div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-4 space-y-1">
          <NavLink
            to="/superadmin/tenants"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              } ${collapsed ? 'justify-center' : ''}`
            }
            title={collapsed ? 'Tenants' : undefined}
          >
            <Building2 size={18} className="shrink-0" />
            {!collapsed && <span>Tenants</span>}
          </NavLink>
          <NavLink
            to="/superadmin/system-templates"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              } ${collapsed ? 'justify-center' : ''}`
            }
            title={collapsed ? 'System templates' : undefined}
          >
            <ImageIcon size={18} className="shrink-0" />
            {!collapsed && <span>System templates</span>}
          </NavLink>
          <NavLink
            to="/superadmin/email-templates"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              } ${collapsed ? 'justify-center' : ''}`
            }
            title={collapsed ? 'Email templates' : undefined}
          >
            <Mail size={18} className="shrink-0" />
            {!collapsed && <span>Email templates</span>}
          </NavLink>
          <NavLink
            to="/superadmin/legal"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              } ${collapsed ? 'justify-center' : ''}`
            }
            title={collapsed ? 'Legal documents' : undefined}
          >
            <Scale size={18} className="shrink-0" />
            {!collapsed && <span>Legal documents</span>}
          </NavLink>
          <NavLink
            to="/superadmin/cancellations"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              } ${collapsed ? 'justify-center' : ''}`
            }
            title={collapsed ? 'Cancellations' : undefined}
          >
            <UserMinus size={18} className="shrink-0" />
            {!collapsed && <span>Cancellations</span>}
          </NavLink>
          <NavLink
            to="/superadmin/support"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              } ${collapsed ? 'justify-center' : ''}`
            }
            title={collapsed ? 'Support' : undefined}
          >
            <Headset size={18} className="shrink-0" />
            {!collapsed && <span>Support</span>}
          </NavLink>
          <NavLink
            to="/superadmin/monitoring"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              } ${collapsed ? 'justify-center' : ''}`
            }
            title={collapsed ? 'Monitoring' : undefined}
          >
            <Activity size={18} className="shrink-0" />
            {!collapsed && <span>Monitoring</span>}
          </NavLink>
          <NavLink
            to="/superadmin/sites"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              } ${collapsed ? 'justify-center' : ''}`
            }
            title={collapsed ? 'Websites' : undefined}
          >
            <PanelsTopLeft size={18} className="shrink-0" />
            {!collapsed && <span>Websites</span>}
          </NavLink>
          <NavLink
            to="/superadmin/changelog"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              } ${collapsed ? 'justify-center' : ''}`
            }
            title={collapsed ? 'Changelog' : undefined}
          >
            <ClipboardList size={18} className="shrink-0" />
            {!collapsed && <span>Changelog</span>}
          </NavLink>
          <NavLink
            to="/superadmin/release-notes"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              } ${collapsed ? 'justify-center' : ''}`
            }
            title={collapsed ? 'Release notes' : undefined}
          >
            <Megaphone size={18} className="shrink-0" />
            {!collapsed && <span>Release notes</span>}
          </NavLink>
        </nav>

        {/* Bottom */}
        <div className="border-t border-slate-700 p-2 space-y-1">
          {!collapsed && (
            <div className="px-3 py-2 text-xs text-slate-500 truncate">{user?.email}</div>
          )}
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 w-full px-3 py-2 rounded-md text-sm text-slate-400 hover:bg-slate-800 hover:text-white transition-colors cursor-pointer ${collapsed ? 'justify-center' : ''}`}
            title={collapsed ? 'Sign out' : undefined}
          >
            <LogOut size={18} className="shrink-0" />
            {!collapsed && <span>Sign out</span>}
          </button>
          <button
            onClick={() => setCollapsed(c => !c)}
            className={`flex items-center gap-3 w-full px-3 py-2 rounded-md text-sm text-slate-400 hover:bg-slate-800 hover:text-white transition-colors cursor-pointer ${collapsed ? 'justify-center' : ''}`}
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            {!collapsed && <span className="text-xs">Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        <div className={`mx-auto px-8 py-10 ${isWidePage ? 'max-w-none' : 'max-w-5xl'}`}>
          <Outlet />
        </div>
      </main>
    </div>
  )
}
